import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Send, ImagePlus, X, ShieldAlert, ArrowRight, Loader2, Sparkles, Camera, Wrench, CalendarClock, HelpCircle, CheckCircle2, MessageCircle } from "lucide-react";
import { api, uploadFile, mediaUrl } from "@/lib/api";
import { Events } from "@/lib/analytics";

const INTENT_OPENERS = {
  photo: { title: "Analyze a Photo", line: "Upload a photo of the area and I'll point out what I'm likely seeing — honestly, with no overclaiming." },
  service: { title: "Find My Service", line: "Tell me a little about your property and I'll match you to the right Ascendia service." },
  estimate: { title: "Estimate My Property", line: "Share your service, size and condition and I'll give you an estimated range using our pricing." },
  ask: { title: "Ask Anything", line: "Ask me anything about cleaning, detailing, stains, surfaces or preparing your property." },
  plan: { title: "Ask About Your Property Plan", line: "I have your Property Plan. Ask me why a service was recommended, what I'd detail, or anything about the scope and estimate." },
};

const START_CHIPS = {
  photo: ["I'll upload a photo of the mess", "What can you tell from a photo?"],
  service: ["I just finished renovating my apartment", "I'm moving out of a rental", "I want a deep clean before guests arrive"],
  estimate: ["120 sqm apartment, post-construction, empty", "80 sqm, previously lived in, 2 bathrooms"],
  ask: ["How do I remove limescale safely?", "What's the difference between cleaning and detailing?", "Can you get rid of construction dust?"],
  plan: ["Why was this service recommended?", "What would you detail in my property?", "What's included at this level?"],
};

const DIFFICULTY_STYLE = {
  Easy: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  Moderate: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  Hard: "text-orange-300 border-orange-500/30 bg-orange-500/10",
  Professional: "text-gold-soft border-[color:var(--border-strong)] bg-gold/10",
};

// -------- Photo analysis card --------
const PhotoAnalysis = ({ pa }) => (
  <div className="rounded-2xl border border-[color:var(--border-strong)] bg-white/[0.02] p-4" data-testid="ask-photo-analysis">
    <div className="flex items-center gap-2 text-gold-soft text-xs uppercase tracking-[0.14em] mb-3"><Camera className="h-4 w-4" /> What I'm likely seeing</div>
    <div className="space-y-3">
      {pa.likely_issues.map((it, i) => (
        <div key={`${it.issue}-${i}`} className="rounded-xl border border-[color:var(--border-subtle)] bg-black/30 p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{it.issue}</span>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${DIFFICULTY_STYLE[it.difficulty] || DIFFICULTY_STYLE.Moderate}`}>{it.difficulty}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${it.diy_or_pro === "DIY" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" : "text-gold-soft border-[color:var(--border-strong)] bg-gold/10"}`}>{it.diy_or_pro}</span>
            </div>
          </div>
          {it.surface && <p className="mt-1 text-xs text-muted-c">Surface: {it.surface}</p>}
          {it.approach && <p className="mt-1.5 text-sm text-secondary">{it.approach}</p>}
        </div>
      ))}
    </div>
    {pa.uncertainty && <p className="mt-3 text-xs text-muted-c italic flex gap-1.5"><HelpCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {pa.uncertainty}</p>}
  </div>
);

// -------- Service recommendation + estimate card --------
const ServiceCard = ({ data, onHandoff, onWindows }) => {
  const est = data.estimate;
  return (
    <div className="rounded-2xl border border-[color:var(--border-strong)] bg-gradient-to-b from-gold/[0.06] to-transparent p-4" data-testid="ask-service-card">
      <div className="flex items-center gap-2 text-gold-soft text-xs uppercase tracking-[0.14em] mb-2"><Wrench className="h-4 w-4" /> Recommended service</div>
      <p className="font-display text-lg font-semibold text-white">{data.recommended_service}</p>
      {data.recommend_reason && <p className="mt-1 text-sm text-secondary">{data.recommend_reason}</p>}
      {est && (
        <div className="mt-3 rounded-xl border border-[color:var(--border-subtle)] bg-black/30 p-3" data-testid="ask-estimate">
          <p className="text-xs uppercase tracking-[0.14em] text-muted-c">Estimated range</p>
          <p className="mt-1 font-display text-2xl font-semibold text-white">{est.currency}{est.low.toLocaleString()} – {est.currency}{est.high.toLocaleString()}</p>
          <p className="text-[11px] text-muted-c mt-0.5">Estimated range only · your final quote depends on the actual condition & photos you share.</p>
        </div>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button onClick={onHandoff} data-testid="ask-handoff-button" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">Have Ascendia Handle It <ArrowRight className="h-4 w-4" /></button>
        <button onClick={onWindows} data-testid="ask-windows-button" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-strong)] px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors"><CalendarClock className="h-4 w-4 text-gold-soft" /> See open windows</button>
      </div>
    </div>
  );
};

const AssistantBlock = ({ data, onChip, onHandoff, onWindows }) => (
  <div className="mr-auto max-w-[94%] space-y-3">
    <div className="rounded-2xl rounded-tl-sm bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3">
      <p className="text-sm text-secondary whitespace-pre-line leading-relaxed">{data.answer}</p>
    </div>

    {data.photo_analysis && <PhotoAnalysis pa={data.photo_analysis} />}

    {data.risk_warnings?.length > 0 && (
      <div className="rounded-2xl bg-[#2a1d10]/60 border border-[color:var(--border-strong)] px-4 py-3">
        <div className="flex items-center gap-2 text-gold-soft text-xs uppercase tracking-[0.14em] mb-2"><ShieldAlert className="h-4 w-4" /> Safety notes</div>
        <ul className="space-y-1">{data.risk_warnings.map((w, i) => <li key={`${w}-${i}`} className="text-sm text-secondary flex gap-2"><span className="text-gold">•</span>{w}</li>)}</ul>
      </div>
    )}

    {data.recommended_service && <ServiceCard data={data} onHandoff={onHandoff} onWindows={onWindows} />}

    {/* Estimate on its own if there's an estimate but no service card was shown */}
    {!data.recommended_service && data.estimate && (
      <div className="rounded-2xl border border-[color:var(--border-strong)] bg-black/30 p-4" data-testid="ask-estimate">
        <p className="text-xs uppercase tracking-[0.14em] text-muted-c">Estimated range</p>
        <p className="mt-1 font-display text-2xl font-semibold text-white">{data.estimate.currency}{data.estimate.low.toLocaleString()} – {data.estimate.currency}{data.estimate.high.toLocaleString()}</p>
        <button onClick={onHandoff} data-testid="ask-handoff-button" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Have Ascendia Handle It <ArrowRight className="h-4 w-4" /></button>
      </div>
    )}

    {data.availability?.booking_enabled && data.availability.earliest_date && (
      <button onClick={onWindows} className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.02] px-3 py-2 text-xs text-secondary hover:bg-white/5" data-testid="ask-availability-chip">
        <CalendarClock className="h-3.5 w-3.5 text-gold-soft" /> {data.availability.open_days} open days · earliest {new Date(data.availability.earliest_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
      </button>
    )}

    {data.when_to_call_pro && <p className="text-xs text-muted-c px-1">{data.when_to_call_pro}</p>}

    {data.follow_up_questions?.length > 0 && (
      <div className="flex flex-wrap gap-2 pt-1">
        {data.follow_up_questions.map((q, i) => (
          <button key={`${q}-${i}`} onClick={() => onChip(q)} data-testid="ask-followup-chip" className="rounded-full border border-[color:var(--border-strong)] bg-white/[0.03] px-3 py-1.5 text-xs text-white hover:bg-white/5 transition-colors">{q}</button>
        ))}
      </div>
    )}
  </div>
);

export const AskAscendiaChat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent") || "ask";
  const [ctx, setCtx] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { path, url }
  const [uploading, setUploading] = useState(false);
  const known = useRef({});                 // accumulated structured facts
  const collectedMedia = useRef([]);        // every photo the user uploaded {path,url,content_type,original_filename}
  const lastData = useRef(null);            // latest assistant data (for handoff summary)
  const msgId = useRef(0);                  // stable, monotonic message ids for React keys
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const nextId = () => { msgId.current += 1; return msgId.current; };

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  // Honour the initial intent from the homepage entry.
  useEffect(() => {
    Events.askOpened({ entry: intent });
    if (intent === "photo") { const id = setTimeout(() => fileRef.current?.click(), 350); return () => clearTimeout(id); }
    if (intent === "service") setInput("I need help choosing the right service for my property.");
    if (intent === "estimate") setInput("Can you estimate my property? ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context-awareness: seed the assistant with the visitor's saved property session (Property Plan / Estimate).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ascendia_prefill");
      if (!raw) return;
      const p = JSON.parse(raw) || {};
      const sel = (p.ai_collected && p.ai_collected.selections) || {};
      const facts = {
        size_sqm: p.size_sqm || sel.sqm || "",
        bathrooms: p.bathrooms || sel.bathrooms || "",
        rooms: p.rooms || sel.rooms || "",
        furnishing: p.furnishing || sel.furnishing || "",
        property_state: p.property_state || "",
        service_required: p.service_required || "",
      };
      known.current = { ...facts, ...known.current };
      const bits = [facts.size_sqm && `${facts.size_sqm} m²`, facts.property_state, facts.service_required].filter(Boolean);
      if (bits.length) setCtx({ service: facts.service_required, summary: bits.join(" · ") });
    } catch (e) { console.debug("Ask Ascendia: no valid saved session context to seed:", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goHandoff = useCallback(() => {
    const k = known.current || {};
    const d = lastData.current || {};
    const prefill = {
      service_required: d.quote_service || "",
      city: k.city || "",
      size_sqm: k.size_sqm || "",
      bathrooms: k.bathrooms || "",
      rooms: k.rooms || "",
      furnishing: k.furnishing || "",
      property_state: k.property_state || "",
      deadline: k.deadline || "",
      priority_areas: d.lead_summary || "",
      notes: d.lead_summary ? `Shared via Ask Ascendia. ${d.lead_summary}` : "",
      media: collectedMedia.current || [],
      ai_summary: d.lead_summary || "",
      ai_collected: k,
    };
    try { sessionStorage.setItem("ascendia_prefill", JSON.stringify(prefill)); } catch (e) { console.error("Ask Ascendia: could not store prefill", e); }
    navigate("/contact?prefill=ask");
  }, [navigate]);

  const goWindows = useCallback(() => navigate("/request-time"), [navigate]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg && !pendingImage) return;
    const userMsg = { id: nextId(), role: "user", content: msg, image: pendingImage?.url };
    const history = messages.map((m) => ({ role: m.role, content: m.role === "assistant" ? m.data?.answer : m.content }));
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    const imgPath = pendingImage?.path;
    setPendingImage(null);
    setLoading(true);
    try {
      const { data } = await api.post("/ask", { message: msg || "Please look at this photo.", history, image_path: imgPath, known: known.current });
      known.current = { ...known.current, ...(data.collected || {}) };
      lastData.current = data;
      setMessages((prev) => [...prev, { id: nextId(), role: "assistant", data }]);
    } catch (e) {
      console.error("Ask Ascendia request failed:", e);
      setMessages((prev) => [...prev, { id: nextId(), role: "assistant", data: { answer: "Sorry, something went wrong. Please try again or message us on WhatsApp.", risk_warnings: [], follow_up_questions: [], cta_text: "", recommended_service: "" } }]);
    } finally { setLoading(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setPendingImage({ path: res.path, url: mediaUrl(res.url) });
      collectedMedia.current = [...collectedMedia.current, { path: res.path, url: res.url, original_filename: res.original_filename, content_type: res.content_type }];
    } catch (uploadErr) { console.error("Image upload failed in Ask Ascendia:", uploadErr); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const opener = INTENT_OPENERS[intent] || INTENT_OPENERS.ask;
  const baseChips = START_CHIPS[intent] || START_CHIPS.ask;
  // When we have the visitor's property context, surface contextual prompts first.
  const chips = ctx
    ? [
        "Why did Ascendia recommend this?",
        "What would you clean in my kitchen?",
        "Why is my estimate this range?",
        "What should I prepare before the team arrives?",
        "Would Premium be worth it for my property?",
      ]
    : baseChips;

  return (
    <div data-testid="ask-ascendia-chat" className="ascendia-card card-accent-top !bg-[#101011] flex flex-col h-[600px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gold/10 border border-[color:var(--border-strong)]"><Sparkles className="h-6 w-6 text-gold-soft" /></div>
            <p className="font-display text-lg text-white">{opener.title}</p>
            <p className="text-secondary mt-2 max-w-md mx-auto text-sm">{opener.line}</p>
            {ctx && (
              <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--border-strong)] bg-gold/[0.06] px-3 py-1.5 text-xs text-gold-soft" data-testid="ask-context-banner">
                <Sparkles className="h-3.5 w-3.5" /> Based on your Property Plan: {ctx.summary}
              </div>
            )}
            <p className="text-xs text-muted-c mt-3 max-w-md mx-auto">Honest guidance only — I can't be certain from a photo alone, and for anything delicate I'll recommend a professional look.</p>
          </div>
        )}
        {messages.map((m) => (
          m.role === "user" ? (
            <div key={m.id} className="ml-auto max-w-[85%]">
              {m.image && <img src={m.image} alt="upload" className="mb-2 ml-auto max-h-44 rounded-xl border border-[color:var(--border-subtle)]" />}
              {m.content && <div className="rounded-2xl rounded-tr-sm bg-white/[0.08] border border-white/10 px-4 py-3 text-sm text-white">{m.content}</div>}
            </div>
          ) : (
            <AssistantBlock key={m.id} data={m.data} onChip={(q) => send(q)} onHandoff={goHandoff} onWindows={goWindows} />
          )
        ))}
        {loading && <div className="mr-auto flex items-center gap-2 text-muted-c text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Ascendia is thinking…</div>}
      </div>

      {messages.length === 0 && (
        <div className="px-5 pb-2 flex flex-wrap gap-2">
          {chips.map((p) => (
            <button key={p} data-testid="ask-ascendia-prompt-chip" onClick={() => send(p)} className="rounded-full border border-[color:var(--border-subtle)] bg-white/[0.02] px-3 py-1.5 text-xs text-secondary hover:bg-white/5 transition-colors">{p}</button>
          ))}
        </div>
      )}

      <div className="border-t border-[color:var(--border-subtle)] p-3">
        {pendingImage && (
          <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-white/5 px-2 py-1">
            <img src={pendingImage.url} alt="pending" className="h-8 w-8 rounded object-cover" />
            <span className="text-xs text-muted-c">Photo attached</span>
            <button onClick={() => setPendingImage(null)} className="text-muted-c hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} data-testid="ask-ascendia-upload-input" type="file" accept="image/*" className="hidden" onChange={onUpload} />
          <button onClick={() => fileRef.current?.click()} aria-label="Upload photo" className="h-11 w-11 flex-shrink-0 rounded-xl border border-[color:var(--border-subtle)] text-gold-soft hover:bg-white/5 flex items-center justify-center">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          </button>
          <textarea
            data-testid="ask-ascendia-message-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Describe your property or ask a question…"
            className="flex-1 resize-none rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)] max-h-32"
          />
          <button data-testid="ask-ascendia-send-button" onClick={() => send()} disabled={loading} className="h-11 w-11 flex-shrink-0 rounded-xl bg-gold text-black flex items-center justify-center hover:bg-gold-soft disabled:opacity-50 transition-colors">
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskAscendiaChat;
